import React, { useEffect } from 'react';

export const matchSectionId = (baseId: string, id: string) => `${baseId}-section-${id}`;

export const scrollToMatchSection = (baseId: string, id: string) => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.getElementById(matchSectionId(baseId, id))?.scrollIntoView({
    behavior: reducedMotion ? 'auto' : 'smooth',
    block: 'start',
  });
};

export const MatchPageSection: React.FC<{
  baseId: string;
  id: string;
  label: string;
  last?: boolean;
  children: React.ReactNode;
}> = ({ baseId, id, label, last = false, children }) => {
  const sectionId = matchSectionId(baseId, id);

  return (
    <section
      id={sectionId}
      data-match-section={id}
      aria-labelledby={`${sectionId}-heading`}
      className={`scroll-mt-32 space-y-3 ${last ? 'min-h-[calc(100vh-8rem)]' : ''}`}
    >
      <h2 id={`${sectionId}-heading`} className="text-xl font-bold text-gray-950 dark:text-white">{label}</h2>
      {children}
    </section>
  );
};

interface MatchSectionItem<T extends string> {
  id: T;
  label: string;
}

interface MatchSectionNavigationProps<T extends string> {
  baseId: string;
  sections: ReadonlyArray<MatchSectionItem<T>>;
  analysis: { label: string };
  showAnalysis?: boolean;
  active: T;
  analysisOpen: boolean;
  observe: boolean;
  onSelect: (id: T) => void;
  onAnalysisSelect: () => void;
  onVisibleSectionChange: (id: T) => void;
}

export const MatchSectionNavigation = <T extends string>({
  baseId,
  sections,
  analysis,
  showAnalysis = true,
  active,
  analysisOpen,
  observe,
  onSelect,
  onAnalysisSelect,
  onVisibleSectionChange,
}: MatchSectionNavigationProps<T>) => {
  useEffect(() => {
    if (!observe) return undefined;

    const observer = new IntersectionObserver(entries => {
      const nearest = entries
        .filter(entry => entry.isIntersecting)
        .sort((left, right) => Math.abs(left.target.getBoundingClientRect().top) - Math.abs(right.target.getBoundingClientRect().top))[0];
      const id = nearest?.target.getAttribute('data-match-section') as T | null;
      if (id) onVisibleSectionChange(id);
    }, {
      rootMargin: '-128px 0px -65% 0px',
      threshold: [0, 1],
    });

    sections.forEach(section => {
      const element = document.getElementById(matchSectionId(baseId, section.id));
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [baseId, observe, onVisibleSectionChange, sections]);

  return (
    <nav aria-label="Match sections" className="overflow-x-auto">
      <div className="flex min-w-max items-center gap-1">
        {sections.map(section => (
          <button
            key={section.id}
            type="button"
            aria-current={active === section.id ? 'location' : undefined}
            onClick={() => {
              onSelect(section.id);
              scrollToMatchSection(baseId, section.id);
            }}
            className={`min-h-11 rounded-lg px-3 py-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 ${!analysisOpen && active === section.id ? 'bg-club-blue-700 text-white' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800'}`}
          >
            {section.label}
          </button>
        ))}
        {showAnalysis && <button
          type="button"
          aria-expanded={analysisOpen}
          onClick={onAnalysisSelect}
          className={`min-h-11 rounded-lg px-3 py-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 ${analysisOpen ? 'bg-club-blue-700 text-white' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800'}`}
        >
          {analysis.label}
        </button>}
      </div>
    </nav>
  );
};
